import jsPDF from "jspdf";
import {
    aggregateCheckbox,
    aggregateOptions,
    collectCommentsWithDetails,
    drawTable,
    drawHorizontalBarChart
} from "_components/organisms/feedback/surveys-utils";
import moment from "moment";

/**
 * Generates a CAA Survey Report PDF with charts and tables
 * @param {Object} survey - The survey configuration with questions
 * @param {Array} responses - Array of survey responses
 * @param {Object} options - Additional options (dateRange, iata)
 * @returns {boolean} - Returns true if PDF was generated successfully
 */
export const generateCaaSurveyReport = (survey, responses, options = {}) => {
    if (!survey || !survey.questions) {
        console.error("Invalid survey data");
        return false;
    }

    if (!responses || responses.length === 0) {
        console.warn("No responses to generate report");
        return false;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(survey.groupTitle || "CAA Survey Report", pageWidth / 2, y, { align: "center" });
    y += 12;

    // Report metadata
    doc.setFontSize(11);
    doc.setFont(undefined, "normal");
    doc.text(`Airport: ${survey.iata || options.iata || "N/A"}`, 14, y);
    y += 6;
    doc.text(`Survey Type: ${survey.surveyType || "CAA Survey"}`, 14, y);
    y += 6;
    doc.text(`Total Responses: ${responses.length}`, 14, y);
    y += 6;

    // Add date range if provided
    if (options.dateRange) {
        doc.text(`Date Range: ${options.dateRange}`, 14, y);
        y += 6;
    }

    doc.text(`Report Generated: ${moment().format("DD/MM/YYYY HH:mm")}`, 14, y);
    y += 12;

    // Horizontal line separator
    doc.setDrawColor(200);
    doc.line(14, y, pageWidth - 14, y);
    y += 10;

    // Process each question
    survey.questions.forEach((q, index) => {
        // Check if we need a new page (need more space for chart + table)
        if (y > 180) {
            doc.addPage();
            y = 20;
        }

        // Question header
        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.setTextColor(0, 0, 0);
        const questionText = q.text?.en || q.text || `Question ${index + 1}`;
        const splitQuestion = doc.splitTextToSize(`${index + 1}. ${questionText}`, pageWidth - 28);
        doc.text(splitQuestion, 14, y);
        y += splitQuestion.length * 6 + 6;

        doc.setFont(undefined, "normal");

        // CUSTOM OPTIONS (single choice / radio)
        if (q.type === "customOptions" && q.options?.en?.length > 0) {
            const stats = aggregateOptions(responses, q.questionId, q.options.en);
            const total = responses.length;

            // Prepare chart data
            const chartData = Object.entries(stats).map(([option, count]) => ({
                name: option,
                value: count,
                percentage: total > 0 ? (count / total) * 100 : 0
            }));

            // Draw chart first
            y = drawHorizontalBarChart(doc, y, chartData, {
                colors: [
                    [76, 175, 80],    // Green
                    [66, 133, 244],   // Blue
                    [251, 192, 45],   // Yellow
                    [77, 182, 172],   // Teal
                    [156, 39, 176],   // Purple
                    [255, 87, 34],    // Orange
                ]
            });
            y += 5;

            // Check for page break before table
            if (y > 240) {
                doc.addPage();
                y = 20;
            }

            // Draw table
            const tableData = Object.entries(stats).map(([option, count]) => [
                option,
                count.toString(),
                total > 0 ? `${((count / total) * 100).toFixed(1)}%` : "0%"
            ]);

            y = drawTable(doc, y, ["Option", "Count", "Percentage"], tableData, {
                headerBgColor: [66, 139, 202]
            });
            y += 8;
        }

        // CHECKBOX / HYBRID-CHECKBOX
        if ((q.type === "hybrid-checkbox" || q.type === "checkbox") && q.options?.en?.length > 0) {
            const stats = aggregateCheckbox(responses, q.questionId, q.options.en);
            const totalSelections = Object.values(stats).reduce((a, b) => a + b, 0);

            // Prepare chart data
            const chartData = Object.entries(stats).map(([option, count]) => ({
                name: option,
                value: count,
                percentage: totalSelections > 0 ? (count / totalSelections) * 100 : 0
            }));

            // Draw chart first
            y = drawHorizontalBarChart(doc, y, chartData, {
                colors: [
                    [92, 184, 92],    // Green
                    [52, 152, 219],   // Blue
                    [241, 196, 15],   // Yellow
                    [26, 188, 156],   // Teal
                    [155, 89, 182],   // Purple
                    [230, 126, 34],   // Orange
                ]
            });
            y += 5;

            // Check for page break before table
            if (y > 240) {
                doc.addPage();
                y = 20;
            }

            // Draw table
            const tableData = Object.entries(stats).map(([option, count]) => [
                option,
                count.toString()
            ]);

            y = drawTable(doc, y, ["Option", "Times Selected"], tableData, {
                headerBgColor: [92, 184, 92]
            });
            y += 8;
        }

        // RATING TYPE
        if (q.type === "rating") {
            const ratingCounts = {};
            const maxStars = q.starsCount || 5;

            // Initialize counts
            for (let i = 1; i <= maxStars; i++) {
                ratingCounts[i] = 0;
            }

            // Count ratings
            responses.forEach(({ answers }) => {
                const rating = answers?.[q.questionId];
                if (rating && ratingCounts[rating] !== undefined) {
                    ratingCounts[rating]++;
                }
            });

            const total = responses.length;

            // Prepare chart data (reverse order for visual appeal - highest stars first)
            const chartData = Object.entries(ratingCounts)
                .reverse()
                .map(([stars, count]) => ({
                    name: `${stars} Star${parseInt(stars) > 1 ? 's' : ''}`,
                    value: count,
                    percentage: total > 0 ? (count / total) * 100 : 0
                }));

            // Draw chart first
            y = drawHorizontalBarChart(doc, y, chartData, {
                colors: [
                    [255, 193, 7],    // Gold/Yellow for 5 stars
                    [255, 152, 0],    // Orange for 4 stars
                    [255, 87, 34],    // Deep orange for 3 stars
                    [244, 67, 54],    // Red for 2 stars
                    [158, 158, 158],  // Gray for 1 star
                ]
            });
            y += 5;

            // Calculate average
            let sum = 0;
            let validCount = 0;
            Object.entries(ratingCounts).forEach(([stars, count]) => {
                sum += parseInt(stars) * count;
                validCount += count;
            });
            const average = validCount > 0 ? (sum / validCount).toFixed(2) : "N/A";

            // Check for page break before table
            if (y > 240) {
                doc.addPage();
                y = 20;
            }

            // Draw table (original order)
            const tableData = Object.entries(ratingCounts).map(([stars, count]) => [
                `${stars} Star${parseInt(stars) > 1 ? 's' : ''}`,
                count.toString(),
                total > 0 ? `${((count / total) * 100).toFixed(1)}%` : "0%"
            ]);

            y = drawTable(doc, y, ["Rating", "Count", "Percentage"], tableData, {
                headerBgColor: [240, 173, 78]
            });

            doc.setFontSize(10);
            doc.setFont(undefined, "italic");
            doc.setTextColor(80, 80, 80);
            doc.text(`Average Rating: ${average}`, 14, y);
            y += 12;
        }

        // MATRIX-SCALE5
        if (q.type === "matrix-scale5" && q.items?.length > 0) {
            const scaleLabels = q.scaleLabels?.en || ["1", "2", "3", "4", "5"];
            const headers = ["Item", ...scaleLabels];
            const tableData = [];

            q.items.forEach((item, itemIndex) => {
                const itemText = item.text?.en || `Item ${itemIndex + 1}`;
                const row = [itemText];

                // Count responses for each scale value
                const scaleCounts = [0, 0, 0, 0, 0];
                responses.forEach(({ answers }) => {
                    const matrixAnswer = answers?.[q.questionId];
                    if (matrixAnswer && matrixAnswer[itemIndex] !== undefined) {
                        const scaleValue = parseInt(matrixAnswer[itemIndex]);
                        if (scaleValue >= 1 && scaleValue <= 5) {
                            scaleCounts[scaleValue - 1]++;
                        }
                    }
                });

                scaleCounts.forEach(count => {
                    row.push(count.toString());
                });
                tableData.push(row);
            });

            // For matrix, we'll draw a mini chart per item
            q.items.forEach((item, itemIndex) => {
                if (y > 230) {
                    doc.addPage();
                    y = 20;
                }

                const itemText = item.text?.en || `Item ${itemIndex + 1}`;
                doc.setFontSize(10);
                doc.setFont(undefined, "bold");
                doc.setTextColor(60, 60, 60);
                doc.text(`• ${itemText}`, 16, y);
                y += 6;

                // Count responses for this item
                const scaleCounts = [0, 0, 0, 0, 0];
                responses.forEach(({ answers }) => {
                    const matrixAnswer = answers?.[q.questionId];
                    if (matrixAnswer && matrixAnswer[itemIndex] !== undefined) {
                        const scaleValue = parseInt(matrixAnswer[itemIndex]);
                        if (scaleValue >= 1 && scaleValue <= 5) {
                            scaleCounts[scaleValue - 1]++;
                        }
                    }
                });

                const totalForItem = scaleCounts.reduce((a, b) => a + b, 0);
                const chartData = scaleLabels.map((label, i) => ({
                    name: label,
                    value: scaleCounts[i],
                    percentage: totalForItem > 0 ? (scaleCounts[i] / totalForItem) * 100 : 0
                }));

                y = drawHorizontalBarChart(doc, y, chartData, {
                    barHeight: 10,
                    barSpacing: 4,
                    labelWidth: 60,
                    showPercentageAxis: false,
                    colors: [
                        [76, 175, 80],
                        [139, 195, 74],
                        [255, 193, 7],
                        [255, 152, 0],
                        [244, 67, 54],
                    ]
                });
                y += 4;
            });

            // Check for page break before summary table
            if (y > 220) {
                doc.addPage();
                y = 20;
            }

            // Draw summary table
            doc.setFontSize(10);
            doc.setFont(undefined, "bold");
            doc.setTextColor(0, 0, 0);
            doc.text("Summary Table:", 14, y);
            y += 6;

            y = drawTable(doc, y, headers, tableData, {
                headerBgColor: [91, 192, 222],
                fontSize: 9
            });
            y += 8;
        }

        // COMMENTS / TEXT - Now displayed as a table with ID, Response, Date
        if (q.type === "comment" || q.type === "text") {
            const commentsWithDetails = collectCommentsWithDetails(responses, q.questionId);

            if (commentsWithDetails.length > 0) {
                // Check if we need a new page
                if (y > 200) {
                    doc.addPage();
                    y = 20;
                }

                doc.setFontSize(10);
                doc.setFont(undefined, "normal");
                doc.setTextColor(80, 80, 80);
                doc.text(`Total comments: ${commentsWithDetails.length}`, 14, y);
                y += 8;

                // Prepare table data
                const tableData = commentsWithDetails.map((item, idx) => {
                    // Format the ID (shorten if too long)
                    let displayId = item.id ? String(item.id) : `#${idx + 1}`;
                    if (displayId.length > 15) {
                        displayId = displayId.substring(0, 12) + '...';
                    }

                    // Truncate long responses for table display
                    let displayResponse = item.response || '';
                    if (displayResponse.length > 50) {
                        displayResponse = displayResponse.substring(0, 47) + '...';
                    }

                    // Format date
                    let displayDate = 'N/A';
                    if (item.date && item.date !== 'N/A') {
                        displayDate = moment(item.date).isValid()
                            ? moment(item.date).format("DD/MM/YYYY")
                            : String(item.date).substring(0, 10);
                    }

                    return [displayId, displayResponse, displayDate];
                });

                // Draw comments table
                y = drawTable(doc, y, ["ID", "Response", "Date"], tableData, {
                    headerBgColor: [108, 117, 125],
                    fontSize: 9
                });

                // If responses were truncated, add full responses on new pages
                const longComments = commentsWithDetails.filter(c => c.response && c.response.length > 50);
                if (longComments.length > 0) {
                    doc.addPage();
                    y = 20;

                    doc.setFontSize(11);
                    doc.setFont(undefined, "bold");
                    doc.setTextColor(0, 0, 0);
                    doc.text(`Full Comments for Question ${index + 1}:`, 14, y);
                    y += 10;

                    longComments.forEach((item, idx) => {
                        if (y > 260) {
                            doc.addPage();
                            y = 20;
                        }

                        doc.setFontSize(9);
                        doc.setFont(undefined, "bold");
                        doc.setTextColor(66, 139, 202);

                        let displayId = item.id ? String(item.id) : `#${idx + 1}`;
                        if (displayId.length > 20) {
                            displayId = displayId.substring(0, 17) + '...';
                        }

                        let displayDate = 'N/A';
                        if (item.date && item.date !== 'N/A') {
                            displayDate = moment(item.date).isValid()
                                ? moment(item.date).format("DD/MM/YYYY")
                                : String(item.date).substring(0, 10);
                        }

                        doc.text(`[${displayId}] - ${displayDate}`, 14, y);
                        y += 5;

                        doc.setFont(undefined, "normal");
                        doc.setTextColor(60, 60, 60);
                        const splitComment = doc.splitTextToSize(item.response, pageWidth - 28);
                        doc.text(splitComment, 14, y);
                        y += splitComment.length * 4 + 6;
                    });
                }

                y += 8;
            } else {
                doc.setFontSize(10);
                doc.setFont(undefined, "italic");
                doc.setTextColor(120, 120, 120);
                doc.text("No comments provided", 16, y);
                y += 8;
            }
        }

        // Add spacing between questions
        y += 6;

        // Draw separator line between questions
        doc.setDrawColor(230);
        doc.line(14, y, pageWidth - 14, y);
        y += 8;
    });

    // Footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont(undefined, "normal");
        doc.setTextColor(100);
        doc.text(
            `Page ${i} of ${pageCount}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: "center" }
        );
    }

    // Generate filename with date
    const dateStr = moment().format("YYYY-MM-DD");
    const filename = `CAA-Survey-Report-${survey.iata || "Airport"}-${dateStr}.pdf`;

    doc.save(filename);
    return true;
};

export default generateCaaSurveyReport;
